import Routes from 'resources/routes.json';
import * as LoanPhase from 'utils/enumerator/enum-loan-phase';
import * as LoanState from 'utils/enumerator/enum-loan-state';
import { Steps, StepsRelatedToProofOfIncome } from 'utils/enumerator/enum-unified-application-underwriting-steps';
import UrlHelper from 'utils/helper/url-helper';
import {
  isRecurring,
  loanHasPendingStep,
  loanHasCompletedStep,
  loanHasApplicationTestTag,
  loanHasProductTypeEqualTo,
  findFirstPendingStep,
  formalizationErrorNeedAccountChange,
  shouldShowMultiOfferScreen,
  shouldRedirectToFailedDisbursement
} from 'utils/helper/loan-helper';
import { ADIANTA_SIM, APROVA_SIM, EMPRESTIMO_SIM, TOP_SIM, DEU_TOP } from 'utils/enumerator/enum-products';
import history from './history';

export function getRouteForLoan(loan, currentRoute, defaultRoute = Routes.LOAN_APPLICATION_START) {
  // Route.
  let route = defaultRoute;

  if (window.location.href.includes('forcePage=true')) {
    history.push(window.location.pathname + history.location.search, history.location.state);
    return route;
  }
  // If there is a loan and a loan state.
  if (loan && loan.state) {
    // If the loan is a DeuTop product.
    if (loanHasProductTypeEqualTo(loan, DEU_TOP)) {
      route = Routes.LOAN_DECLINED;
    }
    // If the loan is in the application phase.
    else if ([LoanPhase.APPLICATION].includes(loan.state.phase.id)) {
      // If it is a short application loan.
      if (
        !isRecurring(loan) &&
        loan?.application?.shortApplication &&
        [
          LoanState.APPLICATION_STARTED,
          LoanState.APPLICATION_CONDITIONS_CALCULATED,
          LoanState.APPLICATION_CONDITIONS_SELECTED,
          LoanState.APPLICATION_CONTRACT_READY,
        ].includes(loan.state.id)
      ) {
        window.location.pathname = Routes.LOAN_APPLICATION_SHORT_APPLICATION.DEFAULT;
      }
      // If it is in the created state.
      else if (
        [
          LoanState.APPLICATION_STARTED,
          LoanState.APPLICATION_AUTO_DECLINE_OVERRULED,
          LoanState.APPLICATION_CONDITIONS_CALCULATED,
        ].includes(loan.state.id)
      ) {
        if (
          isRecurring(loan) &&
          loan.labels2.multivariateTests.includes('2021-01-conditions-unfilled') &&
          loan.application.conditionsSelectedAt === null
        ) {
          route = Routes.LOAN_CUSTOMER_SUMMARY;
        } else {
          // Returns the basic application route.
          route = Routes.LOAN_APPLICATION_START;
        }
      }
      // If it is in the basic application state.
      else if ([LoanState.APPLICATION_CONDITIONS_SELECTED].includes(loan.state.id)) {
        if (
          isRecurring(loan) &&
          loan.labels2.multivariateTests.includes('2021-01-conditions-unfilled') &&
          loan.application.conditionsSelectedAt === null
        ) {
          route = Routes.LOAN_CUSTOMER_SUMMARY;
        } else {
          route = Routes.LOAN_APPLICATION_PRE_CONTRACT;
        }
      }
      // If it is in the contract ready state.
      else if ([LoanState.APPLICATION_CONTRACT_READY].includes(loan.state.id)) {
        const testLabels = [
          '2023-03-insurancePage-offerAssigned',
          '2023-03-insurancePage-offerNotAssigned',
          '2023-05-adiantasim-insurance-show-offer',
        ];

        const containsTestLabel =
          testLabels.some((item) => loan?.labels2?.multivariateTests?.includes(item)) ||
          loanHasApplicationTestTag('2023-03-insurancePage', 'offerAssigned') ||
          loanHasApplicationTestTag('2023-03-insurancePage', 'offerNotAssigned');

        const showInsuranceOffer = sessionStorage.getItem('SHOW_INSURANCE_OFFER');
        const shouldShowInsurancePage = showInsuranceOffer === 'true';

        const startCounterOfferProcess = sessionStorage.getItem('startProcessCounterOfferRecurrent');
        const shouldStartCounterOfferProcess = startCounterOfferProcess === 'true';

        const startOpenFinanceRecurrent = sessionStorage.getItem('startOpenFinanceRecurrent');
        const shouldStartOpenFinanceRecurrent = startOpenFinanceRecurrent === 'true';

        if (containsTestLabel && shouldShowInsurancePage) {
          route = Routes.CUSTOMER_INSURANCE_OFFER_PAGE;
        } else {
          route = Routes.LOAN_APPLICATION_CONTRACT;
        }

        // Route for test new flow recurrent
        if (
          isRecurring(loan)
        ) {
          if (shouldShowInsurancePage) {
            route = Routes.CUSTOMER_INSURANCE_OFFER_PAGE;
          } else if (shouldStartOpenFinanceRecurrent) {
            route = Routes.CUSTOMER_OPEN_FINANCE;
          } else if (shouldStartCounterOfferProcess) {
            route = Routes.LOAN_COUNTER_OFFER_PROCESS;
          }
        }
      }

      // If it is in the contract signed state.
      else if ([LoanState.APPLICATION_CONTRACT_SIGNED].includes(loan.state.id)) {
        // Returns the contract application route.
        route = Routes.LOAN_APPLICATION_POST_CONTRACT;
      }

      // If the provided bank is unnaceptable
      else if ([LoanState.APPLICATION_PENDING_ACCEPTABLE_BANK].includes(loan.state.id)) {
        route = Routes.LOAN_APPLICATION_PENDING_ACCEPTABLE_BANK;
      }

      // If the loan is depending on proof of income.
      else if (loanHasPendingStep(loan, StepsRelatedToProofOfIncome)) {
        const nextRoute = findFirstPendingStep(loan, StepsRelatedToProofOfIncome);
        switch (nextRoute) {
          case Steps.KNOWLEDGE_BASED_AUTHENTICATION:
            route = Routes.LOAN_APPLICATION_KBA_VERIFICATION;
            break;
          case Steps.PROOF_OF_INCOME:
            route = Routes.LOAN_APPLICATION_INCOME_PROF;
            break;
          case Steps.ELETRONIC_BANK_STATEMENT:
            route = Routes.LOAN_APPLICATION_INCOME_EBS;
            break;
          case Steps.PSYCHROMETRIC:
            route = Routes.LOAN_APPLICATION_INCOME_PROF_IA;
            break;
          case Steps.PSYCHROMETRIC_OR_EBS:
            route = Routes.LOAN_APPLICATION_INCOME_PROF_IA_EBS;
            break;
          default:
            route = Routes.LOAN_APPLICATION_INCOME;
            break;
        }

        if (isRecurring(loan) && loanHasProductTypeEqualTo(loan, EMPRESTIMO_SIM)) {
          route = Routes.LOAN_UNDERWRITING;
        }
      }

      // If the loan is depending mobile collateral.
      // else if ([LoanState.APPLICATION_PENDING_MOBILE_COLLATERAL_INSTALLATION].includes(loan.state.id)) {
      //   route = Routes.LOAN_APPLICATION_APP_INSTALLATION;
      // }
      
      else if ([LoanState.UNDERWRITING_PENDING_BIOMETRY_IDENTITY].includes(loan.state.id)) {
        route = Routes.LOAN_APPLICATION_UNICO_IDENTITY;
      } else if (
        [LoanState.APPLICATION_PENDING_DOCUMENT_SUBMISSION].includes(loan.state.id) &&
        (loanHasProductTypeEqualTo(loan, APROVA_SIM) || loanHasProductTypeEqualTo(loan, ADIANTA_SIM))
      ) {
        route = Routes.LOAN_APPLICATION_APROVASIM;
      } else if (loanHasPendingStep(loan, Steps.EXTRA_FORMALIZATION)) {
        route = Routes.LOAN_APPLICATION_CONTRACT_EXTRA_FORMALIZATION;
      } else if ([LoanState.APPLICATION_PENDING_CREDIT_POLICY].includes(loan.state.id)) {
        route = Routes.APPLICATION_PENDING_CREDIT_POLICY;
      }
      // Otherwise.
      else {
        // Returns the underwriting route.
        route = Routes.LOAN_UNDERWRITING;
      }
    }

    // If the loan is in the underwriting phase.
    else if ([LoanPhase.UNDERWRITING].includes(loan.state.phase.id)) {
      // If the loan is depending on proof of income.
      if (loanHasPendingStep(loan, StepsRelatedToProofOfIncome)) {
        const nextRoute = findFirstPendingStep(loan, StepsRelatedToProofOfIncome);
        switch (nextRoute) {
          case Steps.KNOWLEDGE_BASED_AUTHENTICATION:
            route = Routes.LOAN_APPLICATION_KBA_VERIFICATION;
            break;
          case Steps.PROOF_OF_INCOME:
            route = Routes.LOAN_APPLICATION_INCOME_PROF;
            break;
          case Steps.ELETRONIC_BANK_STATEMENT:
            route = Routes.LOAN_APPLICATION_INCOME_EBS;
            break;
          case Steps.PSYCHROMETRIC:
            route = Routes.LOAN_APPLICATION_INCOME_PROF_IA;
            break;
          case Steps.PSYCHROMETRIC_OR_EBS:
            route = Routes.LOAN_APPLICATION_INCOME_PROF_IA_EBS;
            break;
          default:
            route = Routes.LOAN_APPLICATION_INCOME;
            break;
        }

        if (isRecurring(loan) && loanHasProductTypeEqualTo(loan, EMPRESTIMO_SIM)) {
          route = Routes.LOAN_UNDERWRITING;
        }
      }
      else if (shouldShowMultiOfferScreen(loan)) {
        route = Routes.LOAN_APPLICATION_MULTIOFFER;
      }
      // If the loan is depending mobile collateral.
      // else if ([LoanState.APPLICATION_PENDING_MOBILE_COLLATERAL_INSTALLATION].includes(loan.state.id)) {
      //   route = Routes.LOAN_APPLICATION_APP_INSTALLATION;
      // } 
      
      else if ([LoanState.UNDERWRITING_PENDING_BIOMETRY_IDENTITY].includes(loan.state.id)) {
        route = Routes.LOAN_APPLICATION_UNICO_IDENTITY;
      } 
      
      // else if ([LoanState.APPLICATION_PENDING_DATA_CAPTURE_APP_INSTALLATION].includes(loan.state.id) && loanHasProductTypeEqualTo(loan, EMPRESTIMO_SIM)) {
      //   route = Routes.LOAN_APPLICATION_DATA_CAPTURE_APP;
      // } 
      
      else if (loanHasPendingStep(loan, Steps.EXTRA_FORMALIZATION)) {
        route = Routes.LOAN_APPLICATION_CONTRACT_EXTRA_FORMALIZATION;
      } else if ([LoanState.UNDERWRITING_PENDING_TOKEN_CLICK].includes(loan.state.id)) {
        route = Routes.LOAN_PENDING_TOKEN_CLICK;
      } else if ([LoanState.APPROVE_READY].includes(loan.state.id) && !shouldRedirectToFailedDisbursement(loan)) {
        route = Routes.LOAN_APPROVED;
      } else if ([LoanState.APPROVE_READY].includes(loan.state.id) && shouldRedirectToFailedDisbursement(loan)){
        route = Routes.LOADING_PAGE_DISBURSEMENT;
      } else if (loanHasProductTypeEqualTo(loan, TOP_SIM) && loanHasCompletedStep(loan, [Steps.ELETRONIC_BANK_STATEMENT])
        && loanHasPendingStep(loan, [Steps.EXTRA_FORMALIZATION, Steps.MANUAL_IDENTITY_VERIFICATION])) {
        route = Routes.LOAN_APPLICATION_CONTRACT_EXTRA_FORMALIZATION;
        // Otherwise.
      } else if ([LoanState.APPLICATION_PENDING_CREDIT_POLICY].includes(loan.state.id)) {
        route = Routes.APPLICATION_PENDING_CREDIT_POLICY;
      } else if (loanHasProductTypeEqualTo(loan, ADIANTA_SIM)) {
        route = Routes.LOAN_APPLICATION_ADIANTASIM;
      } else {
        // Returns the underwriting route.
        route = Routes.LOAN_UNDERWRITING;
      }
    }
    // If the loan is declined.
    else if ([LoanPhase.DECLINED].includes(loan.state.phase.id)) {
      // Returns the declined route.
      route = Routes.LOAN_DECLINED;
    }
    // If the loan state is approved.
    else if ([LoanPhase.DISBURSEMENT].includes(loan.state.phase.id)) {
      if ([LoanState.FORMALIZATION_FAILED].includes(loan?.state?.id) && formalizationErrorNeedAccountChange(loan) && !shouldRedirectToFailedDisbursement(loan)) {
        route = Routes.LOAN_DISBURSEMENT_FAILED_BANK_ACCOUNT;
      } else if ([LoanState.FORMALIZATION_FAILED].includes(loan?.state?.id) && formalizationErrorNeedAccountChange(loan) && shouldRedirectToFailedDisbursement(loan)) {
        route = Routes.CUSTOMER_BANK_INFO_FAIL;
      } else if ([LoanState.FORMALIZATION_FAILED].includes(loan?.state?.id) && !formalizationErrorNeedAccountChange(loan) && shouldRedirectToFailedDisbursement(loan)) {
        route = Routes.LOAN_APPROVED_PENDING;
      } else if (([LoanState.FORMALIZATION_SUBMITTED, LoanState.FORMALIZATION_COMPLETED, LoanState.DISBURSEMENT_SUBMITTED].includes(loan?.state?.id)) && shouldRedirectToFailedDisbursement(loan)) {
        route = Routes.LOADING_PAGE_DISBURSEMENT;
      } else {
        // Returns the approved route.
        route = Routes.LOAN_APPROVED;
      }
    }
    // If the loan state is on going.
    else if (
      [LoanPhase.REGULAR_RE_PAYMENT, LoanPhase.COLLECTION, LoanPhase.POST_COLLECTION, LoanPhase.RE_PAID].includes(
        loan.state.phase.id,
      )
    ) {
      // Returns the ongoing route.
      if (([LoanState.DISBURSEMENT_COMPLETED, LoanState.CURRENT].includes(loan?.state?.id)) && shouldRedirectToFailedDisbursement(loan)) {
        route = Routes.LOAN_APPROVED;
      } else {
        route =
          currentRoute === Routes.LOAN_CUSTOMER_SUMMARY || currentRoute === Routes.LOAN_CUSTOMER_ONGOING
            ? currentRoute
            : Routes.LOAN_CUSTOMER_SUMMARY;
      }
    } else if ([LoanState.ON_TIME_FULLY_PAID_OFF, LoanState.LATE_FULLY_PAID_OFF].includes(loan.state.id)) {
      route =
        currentRoute === Routes.LOAN_CUSTOMER_SUMMARY || currentRoute === Routes.LOAN_CUSTOMER_ONGOING
          ? currentRoute
          : Routes.LOAN_CUSTOMER_SUMMARY;
    }
  }

  // Returns the route.
  return route;
}

export function goToLoanRoute(loan, currentRoute, defaultRoute = Routes.LOAN_APPLICATION_START) {
  // If the user should be in a different route.
  let route = getRouteForLoan(loan, currentRoute, defaultRoute);

  // If there is a route.
  if (route === Routes.LOAN_APPLICATION_START) {
    const { taxId, mobilePhoneNumber, emailAddress } = loan?.application?.personal ?? '';
    if (
      loan?.id &&
      taxId &&
      taxId !== '' &&
      mobilePhoneNumber &&
      mobilePhoneNumber !== '' &&
      emailAddress &&
      emailAddress !== ''
    ) {
      route = Routes.LOAN_APPLICATION_START_COMPLEMENT;
    } else {
      route = Routes.LOAN_APPLICATION_START;
    }
  }

  route = getUrlByLoan(route, loan);

  // Sets default url parameters
  let urlParams = window.location.search;

  // Trying paymentMethodTest-fillable test
  urlParams = UrlHelper.decorateURLParamsWithTest(
    loan,
    urlParams,
    '2022-04-paymentMethodTest-fillable',
    'paymentMethodTest',
  );

  // Test AB KBA Page
  urlParams = UrlHelper.decorateURLParamsWithTest(loan, urlParams, '2022-06-kbaPageTest-A', 'kbaPageA');
  urlParams = UrlHelper.decorateURLParamsWithTest(loan, urlParams, '2022-06-kbaPageTest-B', 'kbaPageB');

  // Declined component redesign test
  urlParams = UrlHelper.decorateURLParamsWithTest(
    loan,
    urlParams,
    '2023-02-declinedPage-isRedesign',
    'declinedPageRedesign',
  );

  // Select bank with pix key test
  urlParams = UrlHelper.decorateURLParamsWithTest(
    loan,
    urlParams,
    '2023-05-preContract-bankSelect',
    'preContractBankSelect',
  );

  if (window.location.href.includes('forcePage=true')) {
    history.push(window.location.pathname + urlParams, history.location.state);
    return;
  }

  if (route && route !== currentRoute) {
    // Redirect to new route
    history.push(route + urlParams, history.location.state);
  }
}

export function getUrlByLoan(pathObject, loan) {
  const index = getUrlIndex(loan);
  let path = pathObject[index];
  if (!path) {
    path = pathObject.DEFAULT;
  }
  return path;
}

function getUrlIndex(loan) {
  let index = 'DEFAULT';
  if (loan?.labels2) {
    if (isRecurring(loan)) {
      index = 'CLIENT';
    }
  }
  return index;
}
